import {
	type ErrorResponse,
	isRouteErrorResponse,
	Link,
	useParams,
	useRouteError,
} from '@remix-run/react'
import { captureRemixErrorBoundaryError } from '@sentry/remix'
import { getErrorMessage } from '#app/utils/misc.tsx'

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => JSX.Element | null

export function GeneralErrorBoundary({
	defaultStatusHandler = ({ error }) => (
		<p>
			{error.status} {error.data}
		</p>
	),
	statusHandlers,
	unexpectedErrorHandler = (error) => <p>{getErrorMessage(error)}</p>,
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => JSX.Element | null
}) {
	const error = useRouteError()
	captureRemixErrorBoundaryError(error)
	const params = useParams()

	if (typeof document !== 'undefined') {
		console.error(error)
	}

	return (
		<div className="container flex items-center justify-center p-20 text-h2">
			{isRouteErrorResponse(error)
				? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
						error,
						params,
					})
				: unexpectedErrorHandler(error)}
		</div>
	)
}

export function UnauthorizedErrorBoundary({ error }: { error: ErrorResponse }) {
	return (
		<div className="flex flex-col items-center justify-center py-12">
			<h1 className="mb-2 font-display text-h2">Access Denied</h1>
			<p className="mb-4 max-w-prose text-body-md text-ink-fg-soft">
				{error.data?.message ||
					'You do not have permission to access this page.'}
			</p>
			<Link to="/" className="ember-link">
				Go Home!
			</Link>
		</div>
	)
}

export function NotFoundErrorBoundary({ error }: { error: ErrorResponse }) {
	return (
		<div className="flex flex-col items-center justify-center py-12">
			<h1 className="mb-2 font-display text-h2">Not Found</h1>
			<p className="mb-4 max-w-prose text-body-md text-ink-fg-soft">
				{error.data?.message || 'The requested resource could not be found.'}
			</p>
		</div>
	)
}

export const statusHandlers = {
	403: ({ error }: { error: ErrorResponse }) => (
		<UnauthorizedErrorBoundary error={error} />
	),
	404: ({ error }: { error: ErrorResponse }) => (
		<NotFoundErrorBoundary error={error} />
	),
}
